import React, { useState } from "react";
import { FiCheck, FiCopy, FiExternalLink, FiLink } from "react-icons/fi";
import { PREVIEW_SITE_URL, PRODUCTION_SITE_URL, buildPublicUrl } from "../Helper/siteLinks";

type ContentLinksPanelProps = {
	/** Deployed preview paths for this content (from /api/content/deployment-links). */
	previewUrls?: string[] | null;
	/** Deployed production paths for this content. */
	productionUrls?: string[] | null;
	/** The public path the content will have once published (site-relative, from the admin source
	 * of truth), shown as target links when nothing is deployed yet. Never a bare record id. */
	fallbackPath?: string | null;
	className?: string;
};

type Row = { env: string; tone: string; url: string };

/** Copy-to-clipboard button with a transient « Copié » confirmation. */
const CopyLinkButton: React.FC<{ value: string }> = ({ value }) => {
	const [done, setDone] = useState(false);
	return (
		<button
			type="button"
			title="Copier le lien"
			aria-label="Copier le lien"
			onClick={async () => {
				try {
					await navigator.clipboard.writeText(value);
					setDone(true);
					setTimeout(() => setDone(false), 1500);
				} catch {
					/* clipboard unavailable : ignore */
				}
			}}
			className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
		>
			{done ? <FiCheck className="text-emerald-600" /> : <FiCopy />}
			{done ? "Copié" : "Copier"}
		</button>
	);
};

/**
 * Panel listing every public URL (Preview / Production) where a content item
 * is deployed. Each link is shown in FULL (selectable, wrapping) with a copy
 * button and an open-in-new-tab button : built from the deployed-files ground
 * truth so the links are the real ones, on both local and deployed environments.
 */
const ContentLinksPanel: React.FC<ContentLinksPanelProps> = ({
	previewUrls,
	productionUrls,
	fallbackPath,
	className = ""
}) => {
	const preview = (previewUrls ?? []).filter(Boolean);
	const production = (productionUrls ?? []).filter(Boolean);

	const sky = "border-sky-200 bg-sky-50 text-sky-700";
	const emerald = "border-emerald-200 bg-emerald-50 text-emerald-700";
	const rows: Row[] = [
		...preview.map((path, i) => ({
			env: preview.length > 1 ? `Preview ${i + 1}` : "Preview",
			tone: sky,
			url: buildPublicUrl(PREVIEW_SITE_URL, path)
		})),
		...production.map((path, i) => ({
			env: production.length > 1 ? `Production ${i + 1}` : "Production",
			tone: emerald,
			url: buildPublicUrl(PRODUCTION_SITE_URL, path)
		}))
	];

	const deployed = rows.length > 0;
	// Nothing deployed yet: offer the intended target URLs from the stored path.
	const fallbackRows: Row[] =
		!deployed && fallbackPath
			? [
					{ env: "Preview (cible)", tone: sky, url: buildPublicUrl(PREVIEW_SITE_URL, fallbackPath) },
					{ env: "Production (cible)", tone: emerald, url: buildPublicUrl(PRODUCTION_SITE_URL, fallbackPath) }
				]
			: [];
	const shownRows = deployed ? rows : fallbackRows;

	return (
		// Same card chrome as its neighbours ContentVersionsPanel / ContentHistoryPanel (ruled header,
		// uppercase title, padding on the body rather than the root) so the three stack as one block.
		<section className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
			<header className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
				<FiLink className="text-slate-400" />
				<h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Liens de la page</h3>
			</header>
			{shownRows.length === 0 ? (
				<p className="px-4 py-4 text-sm text-slate-500">Cette page n'est pas encore déployée.</p>
			) : (
				<div className="p-4">
					{!deployed && (
						<p className="mb-2 text-xs text-slate-500">
							Non déployée pour l'instant : voici les adresses cibles une fois publiée.
						</p>
					)}
					<ul className="space-y-2">
						{shownRows.map((row, i) => (
							<li
								key={`${row.env}-${i}`}
								className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2"
							>
								<span
									className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${row.tone}`}
								>
									{row.env}
								</span>
								<span className="min-w-0 flex-1 break-all font-mono text-xs text-slate-700 select-all">
									{row.url}
								</span>
								<CopyLinkButton value={row.url} />
								<a
									href={row.url}
									target="_blank"
									rel="noopener noreferrer"
									title="Ouvrir dans un nouvel onglet"
									aria-label="Ouvrir le lien"
									className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
								>
									<FiExternalLink /> Ouvrir
								</a>
							</li>
						))}
					</ul>
				</div>
			)}
		</section>
	);
};

export default ContentLinksPanel;
